import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import type {
  FormFillingAssetApplicationProps,
  FormFillingProps,
} from '@hellodarwin/app-features/lib/features/gin-steps/gin-steps-provider';
import { ClientProgramStatus } from '@hellodarwin/core/lib/features/entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import { useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useAppDispatch, useAppSelector } from '../app/app-hooks';
import {
  addAssetLinks,
  downloadAsset,
  uploadAsset,
} from '../features/api/slices/assets-slice';
import {
  copyApplicationAsset,
  createGinStepFormApplicationItem,
  deleteApplicationAssets,
  fetchGinStepApplicationRelatedAssets,
  renameApplicationAsset,
  updateGinStepFormApplicationItem,
} from '../features/api/slices/gin-steps-slice';
import {
  fetchGrant,
  selectGrantById,
} from '../features/api/slices/grants-slice';
import { selectCompany } from '../features/api/slices/profile-slice';
import {
  GetProgram,
  selectProgramById,
  selectRoadmapClientProgramById,
  selectRoadmapProgramById,
  updateProgramProperties,
} from '../features/api/slices/programs-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../features/api/use-client-api';
import { useApplicationStatusModal } from './use-application-status-modal';

interface UseGinStepDocumentSubmissionProps {
  programId: string;
  relatedAssetType?: string;
}

const useGinStepDocumentSubmission = ({
  programId,
  relatedAssetType,
}: UseGinStepDocumentSubmissionProps) => {
  const dispatch = useAppDispatch();
  const oldApi = useClientApi();
  const api = useNewClientApi();
  const { selectedLocale } = useLocale();
  const { t } = useTranslations();
  const {
    modal: applicationStatusModal,
    close: closeApplicationStatusModal,
    openIfNeeded: openApplicationStatusModalIfNeeded,
  } = useApplicationStatusModal();
  const canonicalProgram = useAppSelector((state) =>
    selectProgramById(state, programId),
  );
  const roadmapProgram = useAppSelector(
    (state) =>
      selectRoadmapProgramById(state, programId) ??
      selectRoadmapClientProgramById(state, programId),
  );
  const program = canonicalProgram ?? roadmapProgram;
  const company = useAppSelector(selectCompany);
  const grant = useAppSelector((state) =>
    program?.program_grant_id
      ? selectGrantById(state, program.program_grant_id)
      : null,
  );

  useEffect(() => {
    if (programId && !canonicalProgram) {
      dispatch(GetProgram({ api: oldApi, programId, locale: selectedLocale }));
    }
  }, [canonicalProgram, dispatch, oldApi, programId, selectedLocale]);

  useEffect(() => {
    if (program?.program_grant_id && !grant) {
      dispatch(
        fetchGrant({
          api: oldApi,
          grantId: program.program_grant_id,
          locale: selectedLocale,
        }),
      );
    }
  }, [dispatch, oldApi, program, grant, selectedLocale]);

  const handleSubmitApplicationItem: FormFillingProps['handleSubmitApplicationItem'] =
    async (item, update, shouldTriggerStatusChange) => {
      try {
        const responseForm = update
          ? await dispatch(
              updateGinStepFormApplicationItem({ api, item, programId }),
            ).unwrap()
          : await dispatch(
              createGinStepFormApplicationItem({
                api,
                ginStepFormApplicationItem: item,
                programId,
              }),
            ).unwrap();

        antdMessage.success(
          update
            ? t('gin_steps|item_update_successful')
            : t('gin_steps|item_create_successful'),
        );

        if (responseForm.program_status_trigger && shouldTriggerStatusChange) {
          if (!program?.program_contact_id) {
            console.error('No contact id found for program');
            return responseForm;
          }

          const baseProperties = {
            status: responseForm.program_status_trigger,
            contact_id: program.program_contact_id,
            program_target_user: program.program_target_user,
          };
          const statusModalOpen = openApplicationStatusModalIfNeeded(
            program.client_program_status,
            responseForm.program_status_trigger as ClientProgramStatus,
            program,
            (properties) => {
              dispatch(
                updateProgramProperties({
                  api,
                  programId,
                  properties: { ...properties, ...baseProperties },
                }),
              );
              flushSync(closeApplicationStatusModal);
            },
          );

          if (!statusModalOpen) {
            dispatch(
              updateProgramProperties({
                api,
                programId,
                properties: baseProperties,
              }),
            );
          }
        }

        return responseForm;
      } catch (error) {
        antdMessage.error(t('gin_steps|item_update_failed'));
        return Promise.reject(error);
      }
    };

  const assetApplicationProps: FormFillingAssetApplicationProps | undefined =
    useMemo(() => {
      const companyName = company?.name ?? company?.email;
      const grantName =
        grant?.grant_title ||
        grant?.grant_display_title ||
        program?.program_name;

      if (!programId || !companyName) return undefined;

      return {
        companyName,
        grantName: grantName || '',
        uploadApplicationAsset: async (uploadFormValues, file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('request', JSON.stringify(uploadFormValues || {}));
          return dispatch(
            uploadAsset({
              api,
              formData,
              records: uploadFormValues.records,
            }),
          ).unwrap();
        },
        renameApplicationAsset: async (asset) => {
          const renamed = await dispatch(
            renameApplicationAsset({ api, asset, programId }),
          ).unwrap();
          return renamed.asset;
        },
        programId,
        copyApplicationAsset: (asset) =>
          dispatch(copyApplicationAsset({ api, asset })).unwrap(),
        fetchGinStepApplicationRelatedAssets: () =>
          dispatch(
            fetchGinStepApplicationRelatedAssets({
              api,
              locale: selectedLocale,
              assetType: relatedAssetType,
              excludeProgramId: programId,
            }),
          ).unwrap(),
        addAssetLinks: (assetId, record, additionalOwners) =>
          dispatch(
            addAssetLinks({ api, assetId, record, additionalOwners }),
          ).unwrap(),
        deleteApplicationAssets: (assetIds, targetProgramId, appItemId) =>
          dispatch(
            deleteApplicationAssets({
              api,
              asset_ids: assetIds,
              program_id: targetProgramId,
              app_item_id: appItemId,
            }),
          ).unwrap(),
      };
    }, [
      dispatch,
      api,
      programId,
      program,
      grant,
      company,
      selectedLocale,
      relatedAssetType,
    ]);

  const triggerProgramStatusDialog = (status: string) => {
    if (!program?.program_contact_id) {
      console.error('No contact id found for program');
      return;
    }
    const baseProperties = {
      status,
      contact_id: program.program_contact_id,
    };

    openApplicationStatusModalIfNeeded(
      program.client_program_status,
      status as ClientProgramStatus,
      program,
      (properties) => {
        dispatch(
          updateProgramProperties({
            api,
            programId,
            properties: { ...properties, ...baseProperties },
          }),
        );
        closeApplicationStatusModal();
      },
    );
  };

  const handleDownloadAsset = (assetId: string, name?: string) =>
    dispatch(
      downloadAsset({ api, asset_id: assetId, nameFromItem: name }),
    ).unwrap();

  return {
    assetApplicationProps,
    handleSubmitApplicationItem,
    triggerProgramStatusDialog,
    handleDownloadAsset,
    applicationStatusModal,
    closeApplicationStatusModal,
    isLoading: Boolean(programId && !program),
  };
};

export default useGinStepDocumentSubmission;
